import { useState, useEffect } from "react";
import { 
  Users, 
  Plus, 
  Trash2, 
  CheckCircle,
  AlertCircle,
  Shield,
  MoreVertical,
  Mail,
  Globe,
  RefreshCw,
  Download,
  Eye
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  getContactLists, 
  getContactListWebsites,
  deleteContactList, 
  verifyContactList 
} from "../lib/api";
import { motion } from "framer-motion";

const StatusBadge = ({ status }) => {
  const config = {
    active: { icon: Users, class: "bg-accent text-muted-foreground", label: "Active" },
    verified: { icon: CheckCircle, class: "badge-success", label: "Verified" },
    verifying: { icon: RefreshCw, class: "badge-warning", label: "Verifying" }
  };
  
  const { icon: Icon, class: className, label } = config[status] || config.active;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

export default function ContactLists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listWebsites, setListWebsites] = useState([]);
  const [loadingWebsites, setLoadingWebsites] = useState(false);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      setLoading(true);
      const response = await getContactLists();
      setLists(response.data);
    } catch (error) {
      toast.error("Failed to fetch contact lists");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (id) => {
    try {
      await verifyContactList(id);
      toast.success("Email verification started");
      fetchLists();
    } catch (error) {
      toast.error("Failed to start verification");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteContactList(id);
      toast.success("Contact list deleted");
      fetchLists();
    } catch (error) {
      toast.error("Failed to delete list");
    }
  };

  const handleView = async (list) => {
    setSelectedList(list);
    setShowViewDialog(true);
    setLoadingWebsites(true);
    try {
      const response = await getContactListWebsites(list.id);
      setListWebsites(response.data);
    } catch (error) {
      toast.error("Failed to load websites");
    } finally {
      setLoadingWebsites(false);
    }
  };

  const handleExport = (list) => {
    // Export emails as CSV
    const emails = listWebsites
      .filter(w => w.contact_email)
      .map(w => `${w.domain},${w.contact_email},${w.quality_score ?? ''},${w.niche || ''}`)
      .join('\n');
    
    const csv = `Domain,Email,Quality,Niche\n${emails}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${list.name.replace(/\s+/g, '_')}_contacts.csv`;
    a.click();
    toast.success("Exported to CSV");
  };

  return (
    <div className="space-y-6" data-testid="contact-lists-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contact Lists</h1>
          <p className="text-muted-foreground mt-1">
            Manage and verify your outreach contact lists
          </p>
        </div>
        <Button variant="outline" onClick={fetchLists} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">How to Create a Contact List</p>
            <p className="text-sm text-muted-foreground mt-1">
              Go to <strong>Websites</strong> page → Select websites using checkboxes → Click <strong>"Create List"</strong> button
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Lists Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="loading-skeleton">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton h-48 rounded-md" />
          ))}
        </div>
      ) : lists.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="empty-state" data-testid="empty-state">
            <Users className="w-16 h-16" strokeWidth={1} />
            <h3 className="text-lg font-semibold mt-4">No contact lists yet</h3>
            <p className="text-sm max-w-sm mt-2">
              Select websites on the Websites page and create a list to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lists-grid">
          {lists.map((list, index) => (
            <motion.div
              key={list.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="bg-card border-border hover:border-primary/30 transition-colors h-full" data-testid={`list-card-${list.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{list.name}</CardTitle>
                      <StatusBadge status={list.status} />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`list-menu-${list.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleView(list)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Contacts
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleVerify(list.id)}>
                          <Shield className="w-4 h-4 mr-2" />
                          Verify Emails
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(list.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {list.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {list.description}
                    </p>
                  )}
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <div className="text-center p-2 bg-accent/50 rounded">
                      <p className="text-lg font-bold">{list.total_contacts}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div className="text-center p-2 bg-accent/50 rounded">
                      <p className="text-lg font-bold text-green-500">{list.verified_contacts}</p>
                      <p className="text-xs text-muted-foreground">Verified</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleView(list)}
                      data-testid={`view-${list.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleVerify(list.id)}
                      data-testid={`verify-${list.id}`}
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      Verify
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* View Contacts Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="view-dialog">
          <DialogHeader>
            <DialogTitle>{selectedList?.name}</DialogTitle>
            <DialogDescription>
              {selectedList?.total_contacts} contacts • {selectedList?.verified_contacts} verified
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {loadingWebsites ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton h-12 rounded" />
                ))}
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Email</th>
                    <th>Quality</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {listWebsites.map((website) => (
                    <tr key={website.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{website.domain}</span>
                        </div>
                      </td>
                      <td>
                        {website.contact_email ? (
                          <span className="font-mono text-sm">{website.contact_email}</span>
                        ) : (
                          <span className="text-muted-foreground">No email</span>
                        )}
                      </td>
                      <td>
                        <span className="score-badge high">{website.quality_score ?? 'N/A'}</span>
                      </td>
                      <td>
                        {website.email_risk_score ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            website.email_risk_score === 'low' ? 'badge-success' :
                            website.email_risk_score === 'medium' ? 'badge-warning' : 'badge-error'
                          }`}>
                            {website.email_risk_score === 'low' && <CheckCircle className="w-3 h-3" />}
                            {website.email_risk_score === 'medium' && <AlertCircle className="w-3 h-3" />}
                            {website.email_risk_score === 'high' && <AlertCircle className="w-3 h-3" />}
                            {website.email_risk_score}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not verified</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
            <Button onClick={() => selectedList && handleExport(selectedList)}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
